import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus, restoreDisclosureFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  /** @type {HTMLElement | null} */
  #previouslyFocused = null;

  /** @type {HTMLDetailsElement | null} */
  #activeSubmenu = null;

  /** Prevents an obsolete animation callback from moving focus. */
  #transitionSequence = 0;

  /** Skips toggle handling when open/close already manages the details element. */
  #suppressToggleFor = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.addEventListener('toggle', this.#onDetailsToggle);
    this.#resetClosedDrawerState();
    this.#setSubmenuIsolation(null);
    this.#setupAnimatedElementListeners();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#transitionSequence += 1;
    this.#activeSubmenu = null;
    this.#setSubmenuIsolation(null);
    this.#resetClosedDrawerState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('toggle', this.#onDetailsToggle);
    removeTrapFocus();
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#activeSubmenu ?? this.refs.details);
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   * @param {Event} [event]
   */
  toggle(event) {
    event?.preventDefault();
    return this.isOpen ? this.close() : this.open(undefined, event);
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector(':scope > summary');

    if (!summary) return;

    event?.preventDefault();

    if (!details.hasAttribute('open')) {
      this.#suppressToggleFor = details;
      details.setAttribute('open', '');
    }

    summary.setAttribute('aria-expanded', 'true');

    const isMainDrawer = details === this.refs.details;

    if (isMainDrawer) {
      this.#previouslyFocused =
        event?.target instanceof Element ? event.target.closest('summary') : summary;
      if (!(this.#previouslyFocused instanceof HTMLElement)) {
        this.#previouslyFocused = summary;
      }

      this.refs.menuDrawer.removeAttribute('inert');
      this.#activeSubmenu = null;
      this.#setSubmenuIsolation(null);
    } else {
      this.#activeSubmenu = details;
      this.#setSubmenuIsolation(details);
    }

    this.#activateDrawerPanel(details, !isMainDrawer);
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector(':scope > summary');

    if (!summary) return;

    const transitionSequence = ++this.#transitionSequence;
    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    const isMainDrawer = details === this.refs.details;

    if (isMainDrawer) {
      removeTrapFocus();
      this.refs.menuDrawer.setAttribute('inert', '');
    } else {
      details.querySelector(':scope > .menu-drawer__submenu')?.setAttribute('inert', '');
    }

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        if (transitionSequence !== this.#transitionSequence) {
          if (isMainDrawer) this.#resetClosedDrawerState();
          return;
        }

        this.#suppressToggleFor = details;
        reset(details);
        if (isMainDrawer) {
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
          this.#activeSubmenu = null;
          this.#setSubmenuIsolation(null);
          this.#resetClosedDrawerState();
          restoreDisclosureFocus(this.#previouslyFocused, this.refs.details.id);
          this.#previouslyFocused = null;
        } else {
          this.#activeSubmenu = null;
          this.#setSubmenuIsolation(null);
          trapFocus(this.refs.menuDrawer);
          summary.focus();
        }
      },
      { subtree: false }
    );
  }

  /**
   * Sync submenu open state when a details element is toggled natively (for example
   * via VoiceOver) instead of through the declarative click handlers.
   *
   * @param {Event} event
   */
  #onDetailsToggle = (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement)) return;
    if (details === this.refs.details) return;
    if (!details.querySelector(':scope > .menu-drawer__submenu')) return;

    if (this.#suppressToggleFor === details) {
      this.#suppressToggleFor = null;
      return;
    }

    if (!this.isOpen) {
      if (details.open) {
        this.#suppressToggleFor = details;
        details.removeAttribute('open');
        details.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'false');
      }
      return;
    }

    if (details.open) {
      if (details.classList.contains('menu-open')) return;

      const summary = details.querySelector(':scope > summary');
      if (!summary) return;

      summary.setAttribute('aria-expanded', 'true');
      this.#activeSubmenu = details;
      this.#setSubmenuIsolation(details);
      this.#activateDrawerPanel(details, true);
      return;
    }

    if (!details.classList.contains('menu-open') && this.#activeSubmenu !== details) return;

    this.#syncNativeSubmenuClose(details);
  };

  /**
   * Applies open classes, traps focus, and moves focus into the active drawer panel.
   *
   * @param {HTMLDetailsElement} details
   * @param {boolean} isSubmenu
   */
  #activateDrawerPanel(details, isSubmenu) {
    const transitionSequence = ++this.#transitionSequence;
    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      if (transitionSequence !== this.#transitionSequence) return;

      details.classList.add('menu-open');

      if (isSubmenu) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(
        drawer || details,
        () => {
          if (transitionSequence !== this.#transitionSequence || !details.hasAttribute('open')) return;

          trapFocus(drawer instanceof HTMLElement ? drawer : this.refs.menuDrawer);
          this.#focusInitialElement(drawer instanceof HTMLElement ? drawer : details);
        },
        { subtree: false }
      );
    });
  }

  /**
   * Cleans up submenu state after a native details close while the main drawer stays open.
   *
   * @param {HTMLDetailsElement} details
   */
  #syncNativeSubmenuClose(details) {
    const summary = details.querySelector(':scope > summary');

    if (!summary) return;

    const transitionSequence = ++this.#transitionSequence;
    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    details.querySelector(':scope > .menu-drawer__submenu')?.setAttribute('inert', '');

    const drawer = details.querySelector('.menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        if (transitionSequence !== this.#transitionSequence) return;

        this.#activeSubmenu = null;
        this.#setSubmenuIsolation(null);
        trapFocus(this.refs.menuDrawer);
        summary.focus();
      },
      { subtree: false }
    );
  }

  /**
   * Clears drawer focus trap and inert state when the main drawer is closed.
   */
  #resetClosedDrawerState() {
    if (this.isOpen) return;

    removeTrapFocus();
    this.refs.menuDrawer.removeAttribute('inert');
    this.refs.details.classList.remove('menu-open');
  }

  /**
   * Keeps only the visible drawer level exposed to focus and assistive technology.
   * @param {HTMLDetailsElement | null} activeDetails
   */
  #setSubmenuIsolation(activeDetails) {
    const submenuDetails = Array.from(this.refs.menuDrawer.querySelectorAll('details')).filter(
      (details) => details.querySelector(':scope > .menu-drawer__submenu')
    );

    for (const details of submenuDetails) {
      const isActive = details === activeDetails;
      const summary = details.querySelector(':scope > summary');
      const submenu = details.querySelector(':scope > .menu-drawer__submenu');

      summary?.toggleAttribute('inert', isActive);
      submenu?.toggleAttribute('inert', !isActive && activeDetails !== null);

      if (!isActive && details.hasAttribute('open')) {
        this.#suppressToggleFor = details;
        reset(details);
      }
    }

    const topLevelMenu = this.refs.menuDrawer.querySelector(
      ':scope > .menu-drawer__navigation > .menu-drawer__menu'
    );

    for (const item of topLevelMenu?.children ?? []) {
      item.toggleAttribute('inert', activeDetails !== null && !item.contains(activeDetails));
    }

    for (const region of this.refs.menuDrawer.querySelectorAll(
      ':scope > .menu-drawer__close-button, :scope > .menu-drawer__utility-links, :scope > .menu-drawer__featured-content'
    )) {
      region.toggleAttribute('inert', activeDetails !== null && !region.contains(activeDetails));
    }

    this.refs.menuDrawer.classList.toggle('menu-drawer--has-submenu-opened', activeDetails !== null);
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Moves focus to the close button inside the active drawer panel.
   *
   * @param {HTMLElement} container
   */
  #focusInitialElement(container) {
    const closeButton = container.querySelector('.menu-drawer__close-button');
    if (closeButton instanceof HTMLElement) {
      closeButton.focus();
    }
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
